import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';

interface HubspotFormEmbedOptions {
    region: string;
    portalId: string;
    formId: string;
    targetSelector: string;
    values?: Record<string, string>;
    onFormSubmitted?: () => void;
}

// Only non-PII prefill keys are propagated through the browser URL — anything
// resembling contact PII (email, name, phone) must be excluded so it does not
// leak through history, referrers or analytics.
const URL_PREFILL_ALLOWED_KEYS = new Set([
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'language',
    'zip',
    'diagnostic_results',
]);

@Injectable({ providedIn: 'root' })
export class HubspotFormEmbedService {
    private readonly _document = inject(DOCUMENT);
    private readonly _ngZone = inject(NgZone);
    private readonly _scriptPromises = new Map<string, Promise<void>>();
    private readonly _submitCallbacks = new Map<string, () => void>();
    private readonly _trustedOrigins = new Set<string>();
    private _messageListenerInstalled = false;

    async createForm(options: HubspotFormEmbedOptions): Promise<void> {
        await this._loadScript(options.region, options.portalId);

        const target = this._document.querySelector<HTMLElement>(options.targetSelector);
        if (target === null) {
            throw new Error(`HubSpot embed target "${options.targetSelector}" not found`);
        }
        target.innerHTML = '';

        if (options.values) {
            this._mergeIntoUrl(options.values);
        }

        const container = this._document.createElement('div');
        container.className = 'hs-form-frame';
        container.setAttribute('data-region', options.region);
        container.setAttribute('data-form-id', options.formId);
        container.setAttribute('data-portal-id', options.portalId);
        target.appendChild(container);

        if (options.onFormSubmitted) {
            this._submitCallbacks.set(options.formId, options.onFormSubmitted);
            // The postMessage comes from the form iframe, not the script loader.
            // HubSpot uses forms.hsforms.com (global/NA) and forms-<region>.hsforms.com (EU).
            this._trustedOrigins.add('https://forms.hsforms.com');
            this._trustedOrigins.add(`https://forms-${options.region}.hsforms.com`);
            this._installMessageListener(options.formId);
        }
    }

    private _loadScript(region: string, portalId: string): Promise<void> {
        const key = `${region}:${portalId}`;
        const existing = this._scriptPromises.get(key);
        if (existing !== undefined) {
            return existing;
        }
        const promise = new Promise<void>((resolve, reject) => {
            const script = this._document.createElement('script');
            script.src = `https://js-${region}.hsforms.net/forms/embed/${portalId}.js`;
            script.defer = true;
            script.onload = (): void => resolve();
            script.onerror = (): void => reject(new Error('Failed to load HubSpot forms embed script'));
            this._document.head.appendChild(script);
        });
        this._scriptPromises.set(key, promise);
        return promise;
    }

    private _mergeIntoUrl(values: Record<string, string>): void {
        const view = this._document.defaultView;
        if (view === null) {
            return;
        }
        const url = new URL(view.location.href);
        for (const [key, value] of Object.entries(values)) {
            if (value === '' || !URL_PREFILL_ALLOWED_KEYS.has(key)) {
                continue;
            }
            url.searchParams.set(key, value);
        }
        view.history.replaceState(view.history.state, '', url.toString());
    }

    private _installMessageListener(formId: string): void {
        if (this._messageListenerInstalled) {
            return;
        }
        this._messageListenerInstalled = true;
        window.addEventListener('hs-form-event:on-submission:success', (event: any) => {
            if (event.detail?.formId === formId) {
                // On force Angular à appliquer le changement visuel immédiatement
                this._ngZone.run(() => {
                    const callback = this._submitCallbacks.get(formId);
                    if (callback) {
                        callback();
                    }
                });
            }
        });
    }
}
